import { ArrowPathIcon } from '@heroicons/react/16/solid';

import { Badge } from '@/components/ui/catalyst/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface EnrichmentConfig {
  color: 'zinc' | 'blue' | 'red' | 'emerald';
  label: string;
  showRetry: boolean;
  tooltip: string;
}

const getEnrichmentConfig = (
  status: string | null | undefined,
): EnrichmentConfig => {
  switch (status) {
    case 'not_enriched':
      return {
        color: 'zinc',
        label: 'Not enriched',
        showRetry: true,
        tooltip: 'Additional data from the web has not been fetched yet',
      };
    case 'enrichment_in_progress':
      return {
        color: 'blue',
        label: 'Enriching...',
        showRetry: false,
        tooltip: 'Currently fetching additional data from the web',
      };
    case 'enrichment_failed':
      return {
        color: 'red',
        label: 'Failed',
        showRetry: true,
        tooltip: 'Could not fetch additional data from the web',
      };
    case 'enrichment_succeeded':
      return {
        color: 'emerald',
        label: 'Enriched',
        showRetry: false,
        tooltip: 'Additional data has been fetched from the web',
      };
    default:
      return {
        color: 'zinc',
        label: 'Unknown',
        showRetry: false,
        tooltip: 'Enrichment status unknown',
      };
  }
};

interface EnrichmentBadgeProps {
  status: string | null | undefined;
  onRetry: () => void;
  isEnriching: boolean;
}

export const EnrichmentBadge = ({
  status,
  onRetry,
  isEnriching,
}: EnrichmentBadgeProps) => {
  const config = getEnrichmentConfig(status);

  return (
    <div className="flex items-center justify-between gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Badge color={config.color} className="text-xs font-normal">
              {config.label}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">{config.tooltip}</TooltipContent>
      </Tooltip>

      {config.showRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={isEnriching}
          className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
          aria-label="Retry enrichment"
        >
          <ArrowPathIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};
